// Submission Intelligence — inbound submission review.
//
// Flow:
//   1. User uploads a submission (email attachment, slip PDF, etc.).
//   2. POST /reinsurance/submissions/intake runs the existing Document AI
//      pipeline (PII gateway → classify → extract).
//   3. Extracted fields drop into a reviewable form; user edits and confirms.
//   4. POST /reinsurance/deals persists the ReinsuranceDeal with a
//      sourceDocument pointer back to the original upload.
//
// Fields are tenant-agnostic strings — no hardcoded lines of business,
// currencies, or structure types. Reviewer fills or edits whatever the AI
// missed.

import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const S = {
  page:    { flex: 1, overflowY: 'auto', padding: '28px 40px', minHeight: 0 },
  h1:      { fontSize: 24, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' },
  sub:     { color: '#64748b', margin: '0 0 24px' },
  card:    { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 22, marginBottom: 18, maxWidth: 960 },
  section: { fontSize: 12, fontWeight: 700, color: '#800020', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 14 },
  row:     { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 },
  field:   { display: 'flex', flexDirection: 'column', gap: 4 },
  label:   { fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6 },
  input:   { padding: '9px 11px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff', color: '#1e293b' },
  ta:      { padding: '9px 11px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff', color: '#1e293b', minHeight: 78, resize: 'vertical', fontFamily: 'inherit' },
  btn:     { padding: '10px 18px', borderRadius: 10, border: 'none', background: '#800020', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  ghost:   { padding: '10px 18px', borderRadius: 10, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  drop:    { border: '2px dashed #cbd5e1', borderRadius: 12, padding: 32, textAlign: 'center', background: '#f8fafc', cursor: 'pointer', color: '#475569' },
  meta:    { display: 'flex', gap: 24, fontSize: 12, color: '#475569', marginBottom: 14, flexWrap: 'wrap' },
  metaKey: { fontWeight: 600, color: '#1e293b' },
  err:     { color: '#dc2626', fontSize: 13, marginTop: 8 },
  ok:      { color: '#166534', fontSize: 13, marginTop: 8 },
};

const FIELDS = [
  { key: 'cedantName',       label: 'Cedant *',            required: true },
  { key: 'insuredName',      label: 'Insured Name' },
  { key: 'lineOfBusiness',   label: 'Line of Business',    placeholder: 'e.g. Property, Marine, Aviation' },
  { key: 'subclass',         label: 'Subclass',            placeholder: 'e.g. Fire, Cargo, Hull' },
  { key: 'geography',        label: 'Geography' },
  { key: 'occupation',       label: 'Occupation / Industry' },
  { key: 'currency',         label: 'Currency (ISO)',      placeholder: 'e.g. USD, EUR, CHF' },
  { key: 'structureType',    label: 'Structure',           placeholder: 'e.g. Quota Share, Surplus, Facultative' },
  { key: 'sumInsured',       label: 'Sum Insured',         type: 'number' },
  { key: 'premium',          label: 'Original Premium',    type: 'number' },
  { key: 'deductible',       label: 'Deductible',          placeholder: 'narrative (e.g. USD 250,000 each and every loss)' },
  { key: 'retentionPct',     label: 'Retention %',         type: 'number' },
  { key: 'cessionPct',       label: 'Cession %',           type: 'number' },
  { key: 'provisionalShare', label: 'Provisional Share %', type: 'number' },
  { key: 'cedingCommission', label: 'Ceding Commission %', type: 'number' },
  { key: 'brokerage',        label: 'Brokerage %',         type: 'number' },
  { key: 'periodStart',      label: 'Period Start',        type: 'date' },
  { key: 'periodEnd',        label: 'Period End',          type: 'date' },
  { key: 'coBroker',         label: 'Co-Broker' },
  { key: 'retrocedent',      label: 'Retrocedent' },
  { key: 'depositPremium',   label: 'Deposit Premium',     type: 'number' },
];

const toDateInputValue = (v) => {
  if (!v) return '';
  try { return new Date(v).toISOString().slice(0, 10); }
  catch (e) { return String(v).slice(0, 10); }
};

const initialForm = () => FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), { terms: '', notes: '' });

const SubmissionIntelligence = () => {
  const history = useHistory();
  const fileRef = useRef(null);
  const [file, setFile]           = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm]           = useState(initialForm());
  const [source, setSource]       = useState(null);   // sourceDocument for the deal
  const [hiddenDraft, setHidden]  = useState({});     // extracted fields not in FIELDS (e.g. referenceNumbers)
  const [classification, setCls]  = useState(null);
  const [pipelineError, setPErr]  = useState(null);
  const [error, setError]         = useState(null);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  const runIntake = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setSaved(false);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(
        `${BASE_URL}/reinsurance/submissions/intake`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const data = resp?.data?.data || {};
      const draft = data.draft || {};
      const next = { ...initialForm() };
      const extras = {};
      // Split the draft: FIELDS-visible keys go into the form; everything
      // else (referenceNumbers, sourceDocument, cedantContact-derived notes,
      // any future hidden fields) gets carried through as-is so saveDeal
      // can merge them back into the payload without losing extraction work.
      Object.entries(draft).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') return;
        if (k in next) {
          next[k] = ['periodStart', 'periodEnd'].includes(k) ? toDateInputValue(v) : v;
        } else if (k === 'sourceDocument') {
          // handled by setSource
        } else if (k === 'notes') {
          // notes may be auto-populated (e.g. "Cedant contact: ...") — put
          // it on the form so the reviewer can see and edit.
          next.notes = v;
        } else {
          extras[k] = v;
        }
      });
      setForm(next);
      setHidden(extras);
      setSource(draft.sourceDocument || null);
      setCls(data.classification || null);
      setPErr(data.pipelineError || null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Intake failed');
    } finally {
      setUploading(false);
    }
  };

  const onChange = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const saveDeal = async () => {
    if (!form.cedantName || !String(form.cedantName).trim()) {
      setError('Cedant is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Merge hidden extras (referenceNumbers etc.) first so form values
      // override on any collision. The form is the reviewer's edited truth.
      const payload = { ...hiddenDraft, ...form };
      // Coerce numerics — the model tolerates strings but Mongo indexing is
      // cleaner with real numbers. Keep in sync with backend NUMERIC_FIELDS.
      [
        'sumInsured', 'premium',
        'retentionPct', 'cessionPct',
        'provisionalShare', 'cedingCommission', 'brokerage',
        'depositPremium',
      ].forEach((k) => {
        if (payload[k] !== '' && payload[k] != null) payload[k] = Number(payload[k]);
        else delete payload[k];
      });
      if (source) payload.sourceDocument = source;
      const resp = await axios.post(`${BASE_URL}/reinsurance/deals`, payload, { withCredentials: true });
      if (resp?.data?.data?._id) {
        setSaved(true);
        setTimeout(() => history.push('/reinsurance/deals'), 700);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to save deal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <div style={S.page}>
        <h1 style={S.h1}>Reinsurance Submission Intake</h1>
        <p style={S.sub}>Upload a submission — email attachment, slip, cover note. AI extracts the structured fields for you to review and confirm.</p>

        {/* ── Step 1: upload ── */}
        <div style={S.card}>
          <div style={S.section}>1 · Upload Submission</div>
          <div
            style={S.drop}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
          >
            {file ? (
              <>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{file.name}</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>{(file.size / 1024).toFixed(0)} KB · click to change</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>Drop a PDF or image here</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>or click to pick a file</div>
              </>
            )}
            <input
              ref={fileRef}
              type='file'
              accept='.pdf,.doc,.docx,.jpg,.jpeg,.png,.tiff'
              onChange={onPick}
              style={{ display: 'none' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center' }}>
            <button style={S.btn} disabled={!file || uploading} onClick={runIntake}>
              {uploading ? 'Extracting…' : 'Extract Fields'}
            </button>
            {classification && (
              <span style={{ fontSize: 12, color: '#475569' }}>
                Classified as <span style={S.metaKey}>{classification.type}</span> · confidence {classification.confidence}%
              </span>
            )}
          </div>
          {pipelineError && (
            <div style={S.err}>Extraction warning: {pipelineError} — fill the form manually below.</div>
          )}
        </div>

        {/* ── Step 2: review + confirm ── */}
        <div style={S.card}>
          <div style={S.section}>2 · Review Extracted Fields</div>
          <div style={S.meta}>
            <span><span style={S.metaKey}>Source:</span> {source?.fileExchangeId ? source.fileExchangeId : '— (manual entry)'}</span>
            {source?.confidence != null && (
              <span><span style={S.metaKey}>Confidence:</span> {(source.confidence * 100).toFixed(0)}%</span>
            )}
          </div>

          <div style={S.row}>
            {FIELDS.map((f) => (
              <div key={f.key} style={S.field}>
                <label style={S.label}>{f.label}</label>
                <input
                  style={S.input}
                  type={f.type || 'text'}
                  placeholder={f.placeholder || ''}
                  value={form[f.key] || ''}
                  onChange={onChange(f.key)}
                />
              </div>
            ))}
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={S.field}>
              <label style={S.label}>Terms</label>
              <textarea style={S.ta} value={form.terms || ''} onChange={onChange('terms')} />
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <div style={S.field}>
              <label style={S.label}>Notes</label>
              <textarea style={S.ta} value={form.notes || ''} onChange={onChange('notes')} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button style={S.btn} onClick={saveDeal} disabled={saving}>{saving ? 'Saving…' : 'Create Deal'}</button>
            <button style={S.ghost} onClick={() => history.push('/reinsurance/deals')}>Cancel</button>
          </div>
          {error && <div style={S.err}>{error}</div>}
          {saved && <div style={S.ok}>Deal created — redirecting…</div>}
        </div>
      </div>
    </AppLayout>
  );
};

export default SubmissionIntelligence;
